import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Tooltip,
} from "@fluentui/react-components";
import {
  Navigation24Regular,
  Home24Regular,
  Home24Filled,
  DataTrending24Regular,
  DataTrending24Filled,
} from "@fluentui/react-icons";

const NAV_WIDTH_COLLAPSED = "48px";
const NAV_WIDTH_EXPANDED = "200px";

const useStyles = makeStyles({
  nav: {
    display: "flex",
    flexDirection: "column",
    //borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    //backgroundColor: tokens.colorNeutralBackground1,
    overflowX: "hidden",
    transition: "width 0.2s ease",
    flexShrink: 0,
  },
  collapsed: {
    width: NAV_WIDTH_COLLAPSED,
  },
  expanded: {
    width: NAV_WIDTH_EXPANDED,
  },
  toggle: {
    margin: tokens.spacingVerticalS,
    alignSelf: "flex-start",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    width: "100%",
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    cursor: "pointer",
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: "nowrap",
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  navItemIcon: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
  },
  navList: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: tokens.spacingHorizontalXS,
  },
});

interface NavItemDef {
  path: string;
  label: string;
  icon: React.ReactElement;
  activeIcon: React.ReactElement;
}

const navItems: NavItemDef[] = [
  {
    path: "/",
    label: "Home",
    icon: <Home24Regular />,
    activeIcon: <Home24Filled />,
  },
  {
    path: "/sales-analytics",
    label: "Sales Analytics",
    icon: <DataTrending24Regular />,
    activeIcon: <DataTrending24Filled />,
  },
];

export const SideNav: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = React.useState(true);

  return (
    <nav
      className={mergeClasses(
        styles.nav,
        expanded ? styles.expanded : styles.collapsed,
      )}
    >
      <div className={styles.toggle}>
        <Button
          appearance="subtle"
          icon={<Navigation24Regular />}
          onClick={() => setExpanded((prev) => !prev)}
          size="small"
        />
      </div>
      <div className={styles.navList}>
        {navItems.map((item) => {
          const isActive =
            item.path === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.path);
          const button = (
            <button
              key={item.path}
              className={mergeClasses(
                styles.navItem,
                isActive && styles.navItemActive,
              )}
              onClick={() => navigate(item.path)}
            >
              <span className={styles.navItemIcon}>
                {isActive ? item.activeIcon : item.icon}
              </span>
              {expanded && <span>{item.label}</span>}
            </button>
          );
          return expanded ? (
            button
          ) : (
            <Tooltip
              key={item.path}
              content={item.label}
              relationship="label"
              positioning="after"
            >
              {button}
            </Tooltip>
          );
        })}
      </div>
    </nav>
  );
};
